import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, FileText } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { formatINR } from "@/lib/format";
import { EmptyState } from "@/components/common/States";

export default function Cart() {
  const { cart, updateQty, removeFromCart, clearCart, cartTotal, b2bMode, serverCart, user } = useStore();
  const navigate = useNavigate();

  if (cart.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16">
        <EmptyState
          title="Your cart is empty"
          message="Explore our spiritual collection and add products to your cart."
          action={<Link to="/shop" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold px-6 py-3">Start Shopping <ArrowRight size={16} /></Link>}
        />
      </div>
    );
  }

  const grand = cartTotal;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-3xl font-serif font-bold text-stone-900">Shopping Cart ({cart.length})</h1>
        <button onClick={() => window.confirm("Remove all items from your cart?") && clearCart()} data-testid="cart-clear" className="text-sm font-semibold text-red-600 inline-flex items-center gap-1"><Trash2 size={14} /> Clear cart</button>
      </div>
      {serverCart?.issues?.length > 0 && (
        <div data-testid="cart-issues" className="mb-4 rounded-lg bg-amber-50 border border-amber-100 text-amber-800 text-sm p-3">{serverCart.issues.map((m) => <div key={m}>{m}</div>)}</div>
      )}
      {!user && <p className="mb-4 text-xs text-stone-500" data-testid="cart-guest-note">You are shopping as a guest — <Link to="/login" className="font-semibold text-secondary">sign in</Link> to save your cart across devices.</p>}
      <div className="grid lg:grid-cols-[1fr_340px] gap-8">
        <div className="space-y-6">
          {(serverCart?.vendors || [{ vendor_slug: "guest", vendor_name: "", items: cart.map((i) => ({ ...i, id: i.slug })) }]).map((group) => (
          <div key={group.vendor_slug || group.vendor_id} data-testid={`cart-vendor-${group.vendor_slug || "guest"}`} className="space-y-3">
            {group.vendor_name && <div className="flex items-center justify-between text-sm"><span className="font-semibold text-stone-900">Sold by <Link to={`/store/${group.vendor_slug}`} className="text-secondary">{group.vendor_name}</Link></span><span className="text-stone-500">Vendor subtotal <b className="text-stone-900">{formatINR(group.subtotal)}</b></span></div>}
          {group.items.map((row) => {
            const item = cart.find((c) => c.slug === row.slug) || row;
            const unit = item.price;
            return (
              <div key={row.id || item.slug} className={`flex gap-4 rounded-xl border bg-card p-4 ${row.unavailable || row.in_stock === false ? "border-red-200" : "border-border"}`} data-testid={`cart-item-${item.slug}`}>
                <Link to={`/product/${item.slug}`} className="h-24 w-24 rounded-lg overflow-hidden bg-muted shrink-0">
                  <img src={item.images?.[0]} alt={item.name} className="w-full h-full object-cover" />
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${item.slug}`} className="font-semibold text-stone-900 hover:text-secondary line-clamp-2">{item.name}</Link>
                  <p className="text-xs text-stone-500 mt-0.5">{item.vendor_name}{row.variant_name && ` · ${row.variant_name}`} · {formatINR(unit)} each</p>
                  {row.pricing_mode === "WHOLESALE" && <p className="text-xs font-semibold text-blue-800 mt-0.5" data-testid={`cart-wholesale-${item.slug}`}>Wholesale price · MOQ {row.moq} units</p>}
                  {row.unavailable && <p className="text-xs font-semibold text-red-600 mt-1" data-testid={`cart-unavailable-${item.slug}`}>No longer available — please remove</p>}
                  {!row.unavailable && row.in_stock === false && <p className="text-xs font-semibold text-red-600 mt-1" data-testid={`cart-outofstock-${item.slug}`}>Only {row.available} in stock</p>}
                  <div className="mt-2 flex items-center justify-between">
                    <div className="inline-flex items-center rounded-lg border border-border">
                      <button onClick={() => updateQty(item.slug, item.qty - (row.pricing_mode === "WHOLESALE" && item.qty <= row.moq ? 0 : 1))} data-testid={`cart-dec-${item.slug}`} className="h-8 w-8 grid place-items-center hover:bg-muted"><Minus size={14} /></button>
                      <span className="w-9 text-center text-sm font-semibold">{item.qty}</span>
                      <button onClick={() => updateQty(item.slug, item.qty + 1)} data-testid={`cart-inc-${item.slug}`} className="h-8 w-8 grid place-items-center hover:bg-muted"><Plus size={14} /></button>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-secondary">{formatINR(unit * item.qty)}</div>
                      <button onClick={() => removeFromCart(item.slug)} data-testid={`cart-remove-${item.slug}`} className="text-xs text-red-500 flex items-center gap-1 ml-auto mt-1"><Trash2 size={12} /> Remove</button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          </div>
          ))}
        </div>

        <aside className="rounded-xl border border-border bg-card p-6 h-fit lg:sticky lg:top-40">
          <h3 className="font-semibold text-stone-900 mb-4">Order Summary</h3>
          <div className="space-y-2 text-sm">
            <Row label="Subtotal" value={formatINR(cartTotal)} />
            <Row label="Shipping & taxes" value="Calculated later" />
            <div className="border-t border-border pt-3 mt-3 flex justify-between font-bold text-lg">
              <span>Total</span><span className="text-secondary" data-testid="cart-grand-total">{formatINR(grand)}</span>
            </div>
          </div>
          <button onClick={() => navigate("/checkout")} data-testid="checkout-btn" className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold py-3 hover:bg-[hsl(28_90%_38%)]">
            <ShoppingBag size={17} /> Proceed to Checkout
          </button>
          {b2bMode && (
            <Link to="/b2b/request-quote" className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-full border border-secondary text-secondary font-semibold py-3">
              <FileText size={16} /> Request B2B Quote
            </Link>
          )}
          <Link to="/shop" className="mt-3 block text-center text-sm text-stone-500 hover:text-secondary">Continue shopping</Link>
        </aside>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between text-stone-600"><span>{label}</span><span className="font-medium text-stone-800">{value}</span></div>
);
